import { isAdmin } from '../../../helpers/roleHelpers';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import {
  CloudUpload,
  Eye,
  FileEarmark,
  Folder2,
  PencilSquare,
  Plus,
  Search,
  Trash,
  X
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests, uploadFileExplorerFile } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import { humanFileSize } from '../../../helpers/global';
import AlertModal from '../../AlertModal';
import FilePopup from '../FilePopup';
import AttachmentDownload from '../AttachmentDownload';
import './FileExplorer.scss';
import { RESOURCE_COLLECTIONS, resourceCollection } from './resourceCollections';

/**
 * Reusable file/folder explorer for a group of files identified by parentFolderName.
 * Pass a different parentFolderName to browse a completely separate set of files.
 */
const FileExplorer = ({ parentFolderName, displayName, organized = false }) => {
  const [collection, setCollection] = useState('all');
  const [loadError, setLoadError] = useState('');
  const requestSequence = useRef(0);
  const rootLabel = displayName || parentFolderName;
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);

  const [renamingNodeId, setRenamingNodeId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [uploadingCount, setUploadingCount] = useState(0);
  const [previewFile, setPreviewFile] = useState(null);

  const fileInputRef = useRef();
  const isSearching = Boolean(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadNodes(currentFolderId, searchQuery);
    return () => { requestSequence.current += 1; };
  }, [parentFolderName, currentFolderId, searchQuery]);

  const loadNodes = async (parentId, search) => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setLoadError('');
    setNodes([]);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.FILE_EXPLORER_LIST,
      requestBody: {
        parentFolderName,
        ...(search ? { search } : { parentId: parentId || undefined })
      }
    });
    if (sequence !== requestSequence.current) return;
    setLoading(false);

    if (error) {
      setLoadError(error);
      return toast.error(error);
    }

    setNodes(response.nodes || []);
    setBreadcrumbs(search ? [] : response.breadcrumbs || []);
  };

  const resetInlineForms = () => {
    setAddingFolder(false);
    setNewFolderName('');
    setRenamingNodeId(null);
    setRenameValue('');
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const onFolderOpen = folderId => {
    resetInlineForms();
    clearSearch();
    setCurrentFolderId(folderId);
  };

  const onCreateFolderClick = async () => {
    if (!newFolderName.trim()) {
      return toast.error('Please enter a folder name');
    }

    setSavingFolder(true);
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.FILE_EXPLORER_FOLDER,
      requestBody: {
        parentFolderName,
        parentId: currentFolderId || undefined,
        name: newFolderName
      }
    });
    setSavingFolder(false);

    if (error) {
      return toast.error(error);
    }

    resetInlineForms();
    loadNodes(currentFolderId, searchQuery);
  };

  const onFilesPicked = async fileList => {
    const filesToUpload = Array.from(fileList || []);
    if (!filesToUpload.length) return;

    setUploadingCount(filesToUpload.length);

    for (const file of filesToUpload) {
      const { error } = await uploadFileExplorerFile({
        parentFolderName,
        parentId: currentFolderId,
        file
      });
      if (error) {
        toast.error(`${file.name}: ${error}`);
      }
      setUploadingCount(count => count - 1);
    }

    loadNodes(currentFolderId, searchQuery);
  };

  const onRenameClick = node => {
    setAddingFolder(false);
    setRenamingNodeId(node._id);
    setRenameValue(node.name);
  };

  const onRenameSubmit = async () => {
    if (!renameValue.trim()) {
      return toast.error('Please enter a name');
    }

    setSavingRename(true);
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.FILE_EXPLORER_WITH_ID(renamingNodeId),
      method: 'PUT',
      requestBody: { name: renameValue }
    });
    setSavingRename(false);

    if (error) {
      return toast.error(error);
    }

    resetInlineForms();
    loadNodes(currentFolderId, searchQuery);
  };

  const onDeleteConfirm = async () => {
    if (!isAdmin() || !nodeToDelete) return;
    setDeleting(true);
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.FILE_EXPLORER_WITH_ID(nodeToDelete._id),
      method: 'DELETE'
    });
    setDeleting(false);
    setNodeToDelete(null);

    if (error) {
      return toast.error(error);
    }

    loadNodes(currentFolderId, searchQuery);
  };

  const onPreviewClick = node =>
    setPreviewFile({
      filename: node.fileName,
      explorerNodeId: node._id,
      contentType: node.mimeType,
      length: node.size,
      metadata: { originalName: node.name }
    });

  const formatPath = path => {
    if (!path || !path.length) return rootLabel;
    return `${rootLabel} / ${path.map(crumb => crumb.name).join(' / ')}`;
  };

  const NodeRow = ({ node }) => {
    const isFolder = node.type === 'folder';

    if (renamingNodeId === node._id) {
      return (
        <div className="d-flex align-items-center p-2 border-bottom">
          <InputGroup size="sm">
            <Form.Control
              autoFocus
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onRenameSubmit()}
            />
            <InputGroup.Append>
              <Button size="sm" variant="success" disabled={savingRename} onClick={onRenameSubmit}>
                Save
              </Button>
              <Button size="sm" variant="secondary" disabled={savingRename} onClick={resetInlineForms}>
                <X size={16} />
              </Button>
            </InputGroup.Append>
          </InputGroup>
        </div>
      );
    }

    const onRowClick = () => {
      if (isFolder) {
        onFolderOpen(node._id);
      } else {
        onPreviewClick(node);
      }
    };

    const stopRowClick = e => e.stopPropagation();

    return (
      <div
        className="d-flex align-items-center p-2 border-bottom file-explorer-row hover"
        role="button"
        tabIndex={0}
        onClick={onRowClick}
        onKeyDown={e => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onRowClick();
          }
        }}
      >
        <div className="d-flex align-items-center flex-grow-1 text-truncate">
          {isFolder ? (
            <Folder2 className="text-primary mr-2 flex-shrink-0" size={18} />
          ) : (
            <FileEarmark className="text-muted mr-2 flex-shrink-0" size={18} />
          )}
          <div className="text-truncate">
            <div className="midFont text-truncate">{node.name}</div>
            {isSearching && (
              <div className="smallFont text-muted text-truncate">{formatPath(node.path)}</div>
            )}
          </div>
          {!isFolder && node.size != null && (
            <span className="smallFont text-muted ml-2 flex-shrink-0">{humanFileSize(node.size)}</span>
          )}
        </div>

        {!isFolder && (
          <>
            <Eye
              className="hover-light mx-1"
              size={16}
              title="Preview"
              onClick={e => {
                stopRowClick(e);
                onPreviewClick(node);
              }}
            />
            <AttachmentDownload file={{filename:node.fileName,explorerNodeId:node._id,metadata:{originalName:node.name},contentType:node.mimeType}}/>
          </>
        )}
        <PencilSquare
          className="hover-light mx-1"
          size={16}
          title="Rename"
          onClick={e => {
            stopRowClick(e);
            onRenameClick(node);
          }}
        />
        {isAdmin() && <Trash
          className="hover-light text-danger mx-1"
          size={16}
          title="Delete"
          onClick={e => {
            stopRowClick(e);
            if (isAdmin()) setNodeToDelete(node);
          }}
        />}
      </div>
    );
  };

  const showCollections = organized && (!currentFolderId || isSearching);
  const visibleNodes = showCollections && collection !== 'all' ? nodes.filter(node => resourceCollection(node) === collection) : nodes;
  const collectionGroups = RESOURCE_COLLECTIONS.map(group => ({ ...group, nodes: visibleNodes.filter(node => resourceCollection(node) === group.id) })).filter(group => group.nodes.length);

  return (
    <div className="file-explorer">
      <div className="d-flex align-items-center flex-wrap mb-2">
        <div className="flex-grow-1 midFont text-truncate">
          <button type="button" className="file-explorer-crumb" onClick={() => { setCollection('all'); onFolderOpen(null); }}>
            {rootLabel}
          </button>
          {!isSearching &&
            breadcrumbs.map((crumb, index) => (
              <span key={crumb._id}>
                <span className="text-muted mx-1">/</span>
                {index === breadcrumbs.length - 1 ? (
                  <span>{crumb.name}</span>
                ) : (
                  <button type="button" className="file-explorer-crumb" onClick={() => onFolderOpen(crumb._id)}>
                    {crumb.name}
                  </button>
                )}
              </span>
            ))}
          {isSearching && <span className="text-muted ml-2">Search results</span>}
        </div>

        <Button
          size="sm"
          variant="outline-primary"
          className="ml-2"
          disabled={addingFolder || isSearching}
          onClick={() => {
            resetInlineForms();
            setAddingFolder(true);
          }}
        >
          <Plus size={16} />
          <span className="align-middle ml-1">New Folder</span>
        </Button>
        <Button
          size="sm"
          variant="primary"
          className="ml-2"
          disabled={uploadingCount > 0 || isSearching}
          onClick={() => fileInputRef.current.click()}
        >
          <CloudUpload size={16} />
          <span className="align-middle ml-1">Upload</span>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => {
            onFilesPicked(e.target.files);
            e.target.value = null;
          }}
        />
      </div>

      <InputGroup size="sm" className="mb-3">
        <Form.Control
          type="search"
          aria-label="Search resource files and folders"
          placeholder="Search files and folders..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
      </InputGroup>

      {showCollections && <nav className="resource-collections" aria-label="Resource categories">{[{id:'all',title:'All resources'}, ...RESOURCE_COLLECTIONS.filter(group => nodes.some(node => resourceCollection(node) === group.id) || collection === group.id)].map(group => <button type="button" key={group.id} aria-pressed={collection === group.id} onClick={() => setCollection(group.id)}><strong>{group.title}</strong><span>{loading ? 'Loading…' : (group.id === 'all' ? nodes.length : nodes.filter(node => resourceCollection(node) === group.id).length)} {isSearching ? 'matches' : 'files / folders'}</span></button>)}</nav>}
      {isSearching && <p className="file-explorer-search-status" role="status">{searchQuery && nodes.length >= 100 ? 'Showing up to 100 matches. Narrow your search to find more specific files.' : `${visibleNodes.length} matching files / folders`} <Button variant="link" size="sm" onClick={clearSearch}>Clear search</Button></p>}
      {loadError && <div className="alert alert-danger" role="alert">{loadError} <Button size="sm" variant="outline-danger" onClick={() => loadNodes(currentFolderId, searchQuery)}>Retry files</Button></div>}
      <Card>
        <Card.Body className="p-0" style={{ borderRadius: '1.2rem', overflow: 'hidden' }}>
          {addingFolder && !isSearching && (
            <div className="p-2 border-bottom">
              <InputGroup size="sm">
                <Form.Control
                  autoFocus
                  placeholder="Folder name"
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && onCreateFolderClick()}
                />
                <InputGroup.Append>
                  <Button size="sm" variant="success" disabled={savingFolder} onClick={onCreateFolderClick}>
                    Create
                  </Button>
                  <Button size="sm" variant="secondary" disabled={savingFolder} onClick={resetInlineForms}>
                    <X size={16} />
                  </Button>
                </InputGroup.Append>
              </InputGroup>
            </div>
          )}

          {uploadingCount > 0 && (
            <div className="p-2 border-bottom smallFont text-muted">
              <Spinner animation="border" size="sm" className="mr-2" />
              Uploading {uploadingCount} file{uploadingCount > 1 ? 's' : ''}...
            </div>
          )}

          {loading ? (
            <div className="p-3 text-center text-muted midFont">
              <Spinner animation="border" size="sm" className="mr-2" />
              {isSearching ? 'Searching...' : 'Loading files...'}
            </div>
          ) : loadError ? null : visibleNodes.length ? (
            showCollections ? collectionGroups.map(group => <section key={group.id} aria-label={group.title}><header className="resource-collection-heading"><h3>{group.title}</h3><p>{group.description}</p></header>{group.nodes.map(node => <NodeRow key={node._id} node={node} />)}</section>) : visibleNodes.map(node => <NodeRow key={node._id} node={node} />)
          ) : (
            !addingFolder && (
              <p className="p-3 mb-0 text-center text-muted midFont">
                {isSearching ? `No files or folders match "${searchQuery}" in this view.` : showCollections && collection !== 'all' ? 'No resources in this category yet. Check All resources for other files.' : 'This folder is empty.'}
              </p>
            )
          )}
        </Card.Body>
      </Card>

      {previewFile && <FilePopup file={previewFile} onHide={() => setPreviewFile(null)} />}

      <AlertModal
        show={!!nodeToDelete}
        onHide={() => setNodeToDelete(null)}
        onDismissClick={() => setNodeToDelete(null)}
        onContinueClick={onDeleteConfirm}
        continueButtonText="Delete"
        showProgress={deleting}
        progressText="Deleting..."
        alertText={
          nodeToDelete
            ? nodeToDelete.type === 'folder'
              ? `Delete the folder "${nodeToDelete.name}" and everything inside it?`
              : `Delete the file "${nodeToDelete.name}"?`
            : ''
        }
      />
    </div>
  );
};

export default FileExplorer;
